import { useState } from 'react';
import { useClients } from 'src/clients/hooks/useClients';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { GroupMember } from '../grouping';
import { DeletionConfig, SummaryFigure } from '../redesign/modal/CashflowModalShell';
import { deletionMessage } from '../redesign/modal/deletion';
import { useAllocations } from '../redesign/modal/useAllocations';
import {
  ownerSeed,
  ownersFrom,
  ownersRefusal,
  sharedWithViewer,
  storedClientIdsOf,
  storedOwnerNames,
} from './jointAllocations';
import { isPartlyRecorded, shareInPounds, sharesAsRecorded } from './owners';
import { CashflowOwner } from './ownerTypes';
import { JointDeletion } from './useJointSaves';

type SummaryPart = NonNullable<SummaryFigure['parts']>[number];
type Convert = (amount: number | null) => number | null;

const asItIs: Convert = amount => amount;

type JointOwnershipOptions = {
  opened: boolean;
  /** What the item is called in the notice and the delete warning, e.g. "life event". */
  noun: string;
  storedId?: string;
  storedOwners: (CashflowOwner & { amountPostTax?: number | null })[] | undefined;
  members: GroupMember[];
  defaultClientId?: string;
  /** The client whose own tab the form was opened from; absent on the family tab. */
  viewingClientId?: string;
  onDelete: (deletion: JointDeletion) => Promise<void>;
};

/**
 * Names clients wherever they come from: the family first, then any client the adviser can open,
 * since an item can be shared by people in different families. The forms stay mounted while
 * closed, so the client list is only fetched once one opens.
 */
function useClientNames(members: GroupMember[], opened: boolean): (clientId: string) => string {
  const { clients } = useClients({}, { enabled: opened });
  const names = new Map([
    ...clients.map(
      client =>
        [client.id, formatName(client.firstName, client.lastName, NameFormat.NATURAL)] as const,
    ),
    ...members.map(
      member =>
        [
          member.clientId,
          formatName(member.firstName, member.lastName, NameFormat.NATURAL),
        ] as const,
    ),
  ]);
  // A client listed without a name is as good as one not listed at all
  return clientId => names.get(clientId) || 'another client';
}

/**
 * Who a cashflow item is held by and how it is split, as its form stands: the people in the table,
 * the notice about who else holds it, this client's part and the part outside the family for the
 * summary, saving with the backend's refusal of the shares, and deleting the whole item. The three
 * forms differ only in their fields and figures.
 */
export function useJointOwnership({
  opened,
  noun,
  storedId,
  storedOwners,
  members,
  defaultClientId,
  viewingClientId,
  onDelete,
}: JointOwnershipOptions) {
  const { allocations, addMember, removeMember, setAmount, refusal, setRefusal } = useAllocations(
    opened,
    ownerSeed(storedOwners),
    defaultClientId,
  );
  const [isSaving, setIsSaving] = useState(false);
  const nameOf = useClientNames(members, opened);

  const owners = ownersFrom(allocations, storedOwners);
  const shares = sharesAsRecorded(owners);
  const familyClientIds = new Set(members.map(member => member.clientId));
  const shared = sharedWithViewer(
    viewingClientId,
    noun,
    allocations,
    storedOwners,
    members,
    nameOf,
  );
  const isOutsideFamily = (owner: CashflowOwner) =>
    !owner.clientId || !familyClientIds.has(owner.clientId);

  // While the shares are only partly typed no save would accept them, so any split shown would be
  // a guess: the parts show no figure until every share is typed or none is.
  const percentageHeldBy = (holds: (owner: CashflowOwner) => boolean): number | null => {
    if (isPartlyRecorded(owners)) return null;
    return owners.reduce(
      (total, owner, index) => (holds(owner) ? total + shares[index].percentage : total),
      0,
    );
  };
  const partOf = (whole: number | null, percentage: number | null) =>
    whole === null || percentage === null ? null : shareInPounds(whole, percentage);

  const viewerPart = (whole: number | null, convert: Convert = asItIs): SummaryPart | undefined =>
    shared && {
      label: `${shared.viewerName}'s share`,
      value: convert(
        partOf(
          whole,
          percentageHeldBy(owner => owner.clientId === viewingClientId),
        ),
      ),
    };
  const outsidePart = (whole: number | null, convert: Convert = asItIs): SummaryPart | undefined =>
    owners.some(isOutsideFamily)
      ? {
          label: 'Outside this family',
          value: convert(partOf(whole, percentageHeldBy(isOutsideFamily))),
        }
      : undefined;

  const save = async (submit: () => Promise<void>) => {
    setIsSaving(true);
    try {
      await submit();
    } catch (error) {
      setRefusal(ownersRefusal(error));
    } finally {
      setIsSaving(false);
    }
  };

  const storedClientIds = storedClientIdsOf(storedOwners);
  const deletion: DeletionConfig | undefined = storedId
    ? {
        onDelete: () => onDelete({ id: storedId, storedClientIds }),
        title: `Delete ${noun}?`,
        message: deletionMessage(noun, storedOwnerNames(storedOwners, nameOf)),
        confirmLabel: `Delete ${noun}`,
      }
    : undefined;

  const noShareRecorded =
    owners.length > 0 && owners.every(owner => owner.ownershipPercentage == null);

  return {
    allocations,
    isSaving,
    save,
    /** Any change to the item may answer a refusal; sent unchanged it would only be refused again. */
    clearRefusal: () => setRefusal(undefined),
    storedClientIds,
    deletion,
    notice: shared?.notice,
    viewerName: shared?.viewerName,
    viewerPart,
    outsidePart,
    /** This client's part, then the part outside the family, each where it applies. */
    shareParts: (whole: number | null, convert: Convert = asItIs) =>
      [viewerPart(whole, convert), outsidePart(whole, convert)].filter(
        (part): part is SummaryPart => !!part,
      ),
    /** The share a blank one stands for: the even split, while no share is recorded. */
    assumedShare: noShareRecorded ? shares[0].percentage : null,
    allocationProps: {
      members,
      allocations,
      nameOf,
      viewingClientId: shared ? viewingClientId : undefined,
      error: refusal,
      onAdd: addMember,
      onRemove: removeMember,
      onAmountChange: setAmount,
    },
  };
}
